import { useAuth } from '@/auth/AuthProvider';
import BootScreen from '@/components/BootScreen';
import { useEffect } from 'react';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { isAuthPending } from '@/lib/authPending';

function hasAuthCallbackParams(): boolean {
  try {
    const { hash, search } = window.location;
    return /(^|#|&)access_token=/.test(hash) || /(^|\?|&)code=/.test(search);
  } catch { return false; }
}

export default function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, initializing, authError } = useAuth();
  const { go } = useNav();

  const bypass = (() => {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get('stub') === '1' || url.searchParams.get('e2e') === '1';
    } catch { return false; }
  })();

  const transient = initializing || isAuthPending() || hasAuthCallbackParams();

  useEffect(() => {
    if (bypass) return;
    if (transient || authError) return;
    if (!user) go(routes.home());
  }, [bypass, transient, authError, user, go]);

  if (!bypass && transient) return <BootScreen />;

  if (authError) {
    return (
      <div className="min-h-screen w-screen flex items-center justify-center px-6">
        <div className="max-w-md space-y-4 text-center">
          <h2 className="text-2xl font-semibold">Can’t verify your session</h2>
          <p className="text-muted-foreground text-sm whitespace-pre-wrap">
            {authError}
          </p>
          <p className="text-sm text-muted-foreground">
            We’re having trouble verifying your session. The server may be temporarily unavailable. Please try again in a moment.
          </p>
          <div className="flex justify-center gap-3">
            <button
              className="bg-primary text-primary-foreground px-4 py-2 rounded"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
            <button
              className="px-4 py-2 rounded border"
              onClick={() => go(routes.home())}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!bypass && !user) return null;
  return <>{children}</>;
}


